<template>
  <div>
    <!-- Header -->
    <div class="mb-6">
      <div class="flex items-baseline gap-3">
        <h1 class="text-xl font-bold tracking-tight text-foreground lg:text-2xl">Tickets Support</h1>
        <span v-if="awaitingCount > 0" class="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800 border border-orange-200">
          {{ awaitingCount }} en attente
        </span>
      </div>
      <p class="mt-2 text-sm text-gray-700">Gérez les tickets clients WHMCS</p>
    </div>

    <!-- Filters -->
    <div class="bg-white shadow rounded-lg p-4 mb-6">
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <!-- Status Filter -->
        <div>
          <label for="status" class="block text-sm font-medium text-gray-700 mb-1">
            Statut
          </label>
          <select
            id="status"
            v-model="filters.status"
            @change="loadTickets"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="awaiting-reply">⏱️ En attente de réponse</option>
            <option value="">Tous les statuts</option>
            <option value="Open">Ouverts</option>
            <option value="Customer-Reply">Réponse client</option>
            <option value="Answered">Répondus</option>
            <option value="Closed">Fermés</option>
            <option value="On Hold">En attente</option>
            <option value="In Progress">En cours</option>
          </select>
        </div>

        <!-- Department Filter -->
        <div>
          <label for="department" class="block text-sm font-medium text-gray-700 mb-1">
            Département
          </label>
          <select
            id="department"
            v-model="filters.departmentId"
            @change="loadTickets"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="">Tous les départements</option>
            <option
              v-for="dept in departments"
              :key="dept.id"
              :value="dept.id"
            >
              {{ dept.name }}
            </option>
          </select>
        </div>

        <!-- Items per page -->
        <div>
          <label for="perPage" class="block text-sm font-medium text-gray-700 mb-1">
            Résultats par page
          </label>
          <select
            id="perPage"
            v-model="filters.limitnum"
            @change="loadTickets"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center h-64">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4">
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">Erreur</h3>
          <div class="mt-2 text-sm text-red-700">{{ error }}</div>
        </div>
      </div>
    </div>

    <!-- Tickets List -->
    <div v-else-if="tickets.length > 0" class="bg-white shadow overflow-hidden sm:rounded-lg">
      <ul role="list" class="divide-y divide-gray-200">
        <li v-for="ticket in tickets" :key="ticket.id">
          <TicketCard :ticket="ticket" />
        </li>
      </ul>
    </div>

    <!-- Empty State -->
    <div v-else class="bg-white shadow rounded-lg p-12 text-center">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900">Aucun ticket</h3>
      <p class="mt-1 text-sm text-gray-500">Aucun ticket ne correspond aux critères de filtrage.</p>
    </div>

    <!-- Pagination -->
    <div v-if="pagination && tickets.length > 0" class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-6 rounded-lg shadow">
      <div class="flex-1 flex justify-between sm:hidden">
        <button
          @click="previousPage"
          :disabled="filters.limitstart === 0"
          class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Précédent
        </button>
        <button
          @click="nextPage"
          :disabled="filters.limitstart + filters.limitnum >= pagination.total"
          class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Suivant
        </button>
      </div>
      <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p class="text-sm text-gray-700">
            Affichage de
            <span class="font-medium">{{ filters.limitstart + 1 }}</span>
            à
            <span class="font-medium">{{ Math.min(filters.limitstart + filters.limitnum, pagination.total) }}</span>
            sur
            <span class="font-medium">{{ pagination.total }}</span>
            tickets
          </p>
        </div>
        <div>
          <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <button
              @click="previousPage"
              :disabled="filters.limitstart === 0"
              class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span class="sr-only">Précédent</span>
              <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
            </button>
            <button
              @click="nextPage"
              :disabled="filters.limitstart + filters.limitnum >= pagination.total"
              class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span class="sr-only">Suivant</span>
              <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
              </svg>
            </button>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { createSupportService } from '@/composables/supportService';
import TicketCard from '@/components/support/TicketCard.vue';
import { getAccessTokenSilently } from '@/auth';

export default {
  name: 'TicketListView',
  components: {
    TicketCard
  },
  data() {
    return {
      tickets: [],
      departments: [],
      pagination: null,
      loading: false,
      error: null,
      filters: {
        status: 'awaiting-reply', // Valeur spéciale pour Open + Customer-Reply
        departmentId: '',
        limitnum: 25,
        limitstart: 0
      },
      supportService: null,
      awaitingCount: 0
    };
  },
  watch: {
    awaitingCount(newCount) {
      // Mettre à jour le titre de la page quand le compteur change
      this.updatePageTitle(newCount);
    }
  },
  async mounted() {
    this.supportService = createSupportService(getAccessTokenSilently);

    await this.loadDepartments();
    await this.loadTickets();
    await this.loadAwaitingCount();

    // Refresh automatique toutes les 30 secondes
    this.interval = setInterval(() => {
      this.loadTickets(false); // false = ne pas afficher le loader
      this.loadAwaitingCount();
    }, 30000);
  },
  beforeUnmount() {
    if (this.interval) {
      clearInterval(this.interval);
    }
    // Restaurer le titre par défaut
    document.title = 'Console Clafoutis';
  },
  methods: {
    async loadTickets(showLoader = true) {
      if (showLoader) {
        this.loading = true;
      }
      this.error = null;

      try {
        // Cas spécial : "En attente de réponse" = All Active Tickets
        if (this.filters.status === 'awaiting-reply') {
          const params = {
            status: 'All Active Tickets',
            limitnum: this.filters.limitnum,
            limitstart: this.filters.limitstart
          };

          if (this.filters.departmentId) {
            params.departmentId = this.filters.departmentId;
          }

          const response = await this.supportService.getTickets(params);

          if (response.success) {
            let allTickets = response.tickets || [];

            // Filtrer uniquement Open et Customer-Reply
            allTickets = allTickets.filter(ticket =>
              ticket.status === 'Open' || ticket.status === 'Customer-Reply'
            );

            // Trier par dernière réponse (les plus anciens en premier = priorité)
            allTickets.sort((a, b) => {
              const dateA = new Date(a.lastreply.replace(' ', 'T'));
              const dateB = new Date(b.lastreply.replace(' ', 'T'));
              return dateA - dateB;
            });

            this.tickets = allTickets;
            this.pagination = response.pagination || {
              total: allTickets.length,
              start: this.filters.limitstart,
              num: this.filters.limitnum
            };
          } else {
            this.error = 'Erreur lors du chargement des tickets';
          }
        } else {
          // Cas normal : un seul statut ou tous les statuts
          const params = {};
          if (this.filters.status) params.status = this.filters.status;
          if (this.filters.departmentId) params.departmentId = this.filters.departmentId;
          params.limitnum = this.filters.limitnum;
          params.limitstart = this.filters.limitstart;

          const response = await this.supportService.getTickets(params);

          if (response.success) {
            this.tickets = response.tickets || [];
            this.pagination = response.pagination || null;
          } else {
            this.error = response.message || 'Erreur lors du chargement des tickets';
          }
        }
      } catch (err) {
        console.error('Error loading tickets:', err);
        this.error = err.response?.data?.message || 'Erreur lors du chargement des tickets';
      } finally {
        if (showLoader) {
          this.loading = false;
        }
      }
    },

    async loadDepartments() {
      try {
        const response = await this.supportService.getDepartments();
        if (response.success) {
          this.departments = response.departments || [];
        }
      } catch (err) {
        console.error('Error loading departments:', err);
      }
    },

    async loadAwaitingCount() {
      try {
        // Utiliser "All Active Tickets" pour un seul appel API
        const response = await this.supportService.getTickets({
          status: 'All Active Tickets',
          limitnum: 1000, // Charger plus pour avoir le compte exact
          limitstart: 0
        });

        if (response.success && response.tickets) {
          // Compter uniquement Open et Customer-Reply
          this.awaitingCount = response.tickets.filter(ticket =>
            ticket.status === 'Open' || ticket.status === 'Customer-Reply'
          ).length;
        }
      } catch (err) {
        console.error('Error loading awaiting count:', err);
      }
    },

    updatePageTitle(count) {
      if (count > 0) {
        document.title = `(${count}) Tickets Support - Console Clafoutis`;
      } else {
        document.title = 'Tickets Support - Console Clafoutis';
      }
    },

    nextPage() {
      if (this.filters.limitstart + this.filters.limitnum < this.pagination.total) {
        this.filters.limitstart += this.filters.limitnum;
        this.loadTickets();
      }
    },

    previousPage() {
      if (this.filters.limitstart >= this.filters.limitnum) {
        this.filters.limitstart -= this.filters.limitnum;
        this.loadTickets();
      }
    }
  }
};
</script>
